import React, { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Button, Input, Select, Tabs, message } from "antd"
import { Icon } from "ui/Icon"
import { fetchExperiences } from "store/experiences"
import ExperienceModal from "./ExperienceModal"
import styles from "./WebsiteIntegration.module.css"
import useActiveGuide from "hooks/useActiveClient"
import { Text } from "ui/Typography"
import { useTheme } from "context/AntdThemeContext"
import { colors } from "themes/variables"
import { AiChatbotWidget } from "./AiChatbotWidget"

const StepNumber = ({ number }) => (
  <div className={styles.stepNumber}>
    <p style={{ color: "black", margin: 0 }}>{number}</p>
  </div>
)

const StepSection = ({ number, title, subtitle, children, icon }) => (
  <div className={styles.stepSection}>
    <StepNumber number={number} />
    <div className={styles.stepContent}>
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        <p style={{ color: "black", fontWeight: 600, margin: 0 }}>{title}</p>
        {icon}
      </div>
      <p style={{ color: "black", fontSize: "14px" }}>{subtitle}</p>
      <div style={{ marginTop: "24px" }}>{children}</div>
    </div>
  </div>
)

export const WebsiteIntegration = () => {
  const [bookingType, setBookingType] = useState("all")
  const [buttonLabel, setButtonLabel] = useState("Book Now")
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedSpecificExperience, setSelectedSpecificExperience] = useState("")
  const dispatch = useDispatch()
  const [t] = useTranslation()
  const { list: experiences, loading } = useSelector((state) => state.experiences)
  const user = useSelector((store) => store.user)
  const { isActive: active } = useActiveGuide()
  const { isSportsman } = useTheme()
  const isSpecificExperience = bookingType === "specific"
  const isCopyEnabled = isSpecificExperience && !selectedSpecificExperience

  // Get theme color for button
  const buttonColor = isSportsman ? colors.sportsmanBrand : colors.sportswomanBrand

  const code = `<button id="e16a3e52-7909-4654-9bd5-60876c075b73-openModalBtn"
        style="padding: 10px 20px; font-size: 16px; cursor: pointer; background-color: ${buttonColor}; color: white; border: none; border-radius: 5px;">
        ${buttonLabel}
    </button>

    <div id="e16a3e52-7909-4654-9bd5-60876c075b73-modal"
        style="display: none; position: fixed; z-index: 9999; inset: 0; max-width: 100vw; width: 100vw; height: 100vh; background: rgba(0, 0, 0, 0.5); justify-content: center; align-items: center;">
        <div id="e16a3e52-7909-4654-9bd5-60876c075b73-modal-content"
            style="position: relative; background-color: rgba(250, 250, 252, 1); padding: 25px 10px; border-radius: 8px; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); max-width: 1400px; width: 90%; max-height: 77vh; height: 800px; box-sizing: border-box;">
            <span id="e16a3e52-7909-4654-9bd5-60876c075b73-closeBtn"
                style="position: absolute; top: 0px; right: 6px; font-size: 30px; cursor: pointer; color: #333; font-weight: bold;">&times;</span>
            <iframe id="e16a3e52-7909-4654-9bd5-60876c075b73-iframe" src=${
              bookingType === "all"
                ? `"${window.location.origin}/#/embedded/experience_listing/${user?.guide?.uuid}"`
                : `"${window.location.origin}/#/embedded/experience_detail/${user?.guide?.uuid}/${selectedSpecificExperience}"`
            }
                frameborder="0" style="width: 100%; height: 100%; border: none;" allow="payment *"></iframe>
        </div>
    </div>
    <div id="exit-confirmation-modal"
        style="display: none; position: fixed; z-index: 10000; left: 0; top: 0; right: 0; width: 100%; height: 100%; justify-content: center; align-items: center;">
        <div
            style="background: white; padding: 24px 32px; border-radius: 12px; max-width: 420px; width: 90%; box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15); font-family: sans-serif; position: relative;">
            <h3 style="margin-top: 0; font-size: 18px; font-weight: 600; color: #333;">Are you sure you want to exit?
            </h3>
            <p style="font-size: 14px; color: #555; margin-top: 8px;">Clicking ‘Exit’ will cause you to lose your
                booking
                progress.</p>
            <div style="margin-top: 24px; display: flex; justify-content: flex-end; gap: 12px;">
                <button id="continue-booking-btn"
                    style="padding: 8px 20px; background-color: white; color: ${buttonColor}; border: 1px solid ${buttonColor}; border-radius: 20px; font-weight: 500; cursor: pointer;">Proceed</button>
                <button id="exit-booking-btn"
                    style="padding: 8px 20px; background-color: ${buttonColor}; color: white; border: none; border-radius: 20px; font-weight: 500; cursor: pointer;">Exit</button>
            </div>
        </div>
    </div>
    <script>
        (function () {
            const modal = document.getElementById("e16a3e52-7909-4654-9bd5-60876c075b73-modal");
            const closeModalBtn = document.getElementById("e16a3e52-7909-4654-9bd5-60876c075b73-closeBtn");
            const iframe = document.getElementById("e16a3e52-7909-4654-9bd5-60876c075b73-iframe");
            const openModalBtn = document.getElementById("e16a3e52-7909-4654-9bd5-60876c075b73-openModalBtn");
            const exitModal = document.getElementById("exit-confirmation-modal");
            const exitBtn = document.getElementById("exit-booking-btn");
            const continueBtn = document.getElementById("continue-booking-btn");
            const disableBodyScroll = () => {
                document.body.style.overflow = "hidden";
            };
            const enableBodyScroll = () => {
                document.body.style.overflow = "";
            };
            openModalBtn.addEventListener("click", () => {
                window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
                setTimeout(() => {
                    modal.style.display = "flex";
                    disableBodyScroll();
                    iframe.src = ${
                      bookingType === "all"
                        ? `"${window.location.origin}/#/embedded/experience_listing/${user?.guide?.uuid}"`
                        : `"${window.location.origin}/#/embedded/experience_detail/${user?.guide?.uuid}/${selectedSpecificExperience}"`
                    };
                }, 300);
            });
            closeModalBtn.addEventListener("click", () => {
                exitModal.style.display = "flex";
            });
            exitBtn.addEventListener("click", () => {
                exitModal.style.display = "none";
                modal.style.display = "none";
                iframe.src = "";
                enableBodyScroll();
            });
            continueBtn.addEventListener("click", () => {
                exitModal.style.display = "none";
            });
        })();
    </script>
 `

  const handleCopy = () => {
    navigator.clipboard
      .writeText(code)
      .then(() => message.success(t("webIntegration.copy_success")))
      .catch(() => message.error(t("webIntegration.copy_failure")))
  }

  useEffect(() => {
    dispatch(fetchExperiences())
  }, [dispatch])

  const tabItems = [
    {
      key: "booking",
      label: t("webIntegration.booking_widget"),
      children: (
        <>
          <div className={styles.buttonGroup}>
            <Button
              type="primary"
              className={`${styles.noShadow} ${bookingType === "all" ? styles.buttonActive : styles.buttonInactive}`}
              style={{ borderRadius: "4px" }}
              onClick={() => {
                setBookingType("all")
                setSelectedSpecificExperience("")
              }}
            >
              {t("webIntegration.all_experience_booking")}
            </Button>
            <Button
              type="primary"
              className={`${styles.noShadow} ${isSpecificExperience ? styles.buttonActive : styles.buttonInactive}`}
              style={{ borderRadius: "4px" }}
              onClick={() => setBookingType("specific")}
            >
              {t("webIntegration.specific_experience_booking")}
            </Button>
          </div>

          <StepSection
            number={1}
            title={t("webIntegration.button_label_title")}
            subtitle={t("webIntegration.button_label_subtitle")}
            icon={<Icon type="feather" name="Info" width={20} height={20} onClick={() => setIsModalOpen(true)} />}
          >
            <label style={{ display: "block", fontWeight: 500 }}>{t("webIntegration.button_label_field")}</label>
            <Input
              placeholder={t("webIntegration.button_label_placeholder")}
              className={styles.responsiveField}
              style={{ width: "500px" }}
              value={buttonLabel}
              onChange={(e) => setButtonLabel(e.target.value)}
            />
          </StepSection>
          {isSpecificExperience && (
            <StepSection
              number={2}
              title={t("webIntegration.select_experience_title")}
              subtitle={t("webIntegration.select_experience_subtitle")}
            >
              <label style={{ display: "block", fontWeight: 500 }}>{t("webIntegration.select_experience_label")}</label>
              <Select
                showSearch
                placeholder="Select Experience"
                className={styles.responsiveField}
                style={{ width: "500px" }}
                loading={loading}
                options={experiences?.map((exp) => ({
                  label: exp.title,
                  value: exp.uuid
                }))}
                onChange={(value) => {
                  setSelectedSpecificExperience(value)
                }}
                filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
              />
            </StepSection>
          )}
          <StepSection
            number={bookingType === "all" ? 2 : 3}
            title={t("webIntegration.copy_code_title")}
            subtitle={t("webIntegration.copy_code_subtitle")}
          >
            <div className={styles.codeBlockContainer}>
              <div className={styles.codeBlockInner}>
                <Button
                  color="primary"
                  variant="outlined"
                  icon={<Icon type="feather" name="Copy" width={16} height={16} />}
                  onClick={active && !isCopyEnabled ? handleCopy : undefined}
                  disabled={!active || isCopyEnabled}
                >
                  {t("webIntegration.copy_code_button")}
                </Button>
              </div>
              {isCopyEnabled && <Text color="red">{t("webIntegration.copy_code_error")}</Text>}
            </div>
          </StepSection>
          <ExperienceModal bookingType={bookingType} buttonName={buttonLabel} open={isModalOpen} onCancel={() => setIsModalOpen(false)} />
        </>
      )
    },
    // {
    //   key: "chatbot",
    //   label: t("webIntegration.ai_chatbot_widget"),
    //   children: <AiChatbotWidget />
    // }
  ]

  return <Tabs defaultActiveKey="booking" items={tabItems} />
}

StepNumber.propTypes = {
  number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
}

StepSection.propTypes = {
  number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  icon: PropTypes.node
}

StepSection.defaultProps = {
  icon: null
}
